import { Trans } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Link } from "@medoma/components/Link/Link";
import {
  cancelTravellingToVisit,
  finishVisit,
  cancelStartedVisit,
  revertFinishedVisit,
  routeKeys,
  startTravellingToVisit,
  startVisit,
  useOngoingVisit,
  useUpcomingVisit,
  useVisit,
  visitKeys,
} from "@/api/Routes";
import {
  FilledButton,
  OutlinedButton,
  PlainButton,
} from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import PinIcon from "@medoma/components/icons/PinIcon";
import styles from "./$visitId.module.scss";
import { StatusBar } from "@/components/StatusBar/StatusBar";
import { DialogPanel } from "@medoma/components/DialogPanel/DialogPanel";
import { useEffect, useRef, useState } from "react";
import {
  deducedError,
  displayErrorMessageAlert,
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
} from "@/Utils/ErrorUtils";
import { Timeline, TimelineItem } from "@/components/Timeline/Timeline";
import { PostRegisterActivity } from "./PostRegisterActivity";
import { z } from "zod";
import { Heading } from "@medoma/components/Heading/Heading";
import { getPatientNameWithStatus } from "@/api/Patients";

import { Text } from "@medoma/components/Text/Text";
import * as Sentry from "@sentry/react";
import { useLingui } from "@lingui/react/macro";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { CallPatientOrRelatives } from "@/pages/ambulating/patients/$patientId/CallPatientOrRelatives";
import { useForm } from "react-hook-form";
import {
  activityOccurrenceStatusSchema,
  visitStatusSchema,
  type IActivityOccurrence,
} from "@medoma/models/activities";
import Checkbox from "@/components/Checkbox/Checkbox";
import { ActivityOccurrenceListItem } from "@/components/ActivityOccurrenceListItem/ActivityOccurrenceListItem";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import { finishCareEpisodeActivityOccurrences } from "@/api/Activities";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import { ProspectActions } from "@/pages/ambulating/patients/$patientId/ProspectActions";
import { PatientSectionLinks } from "@/pages/ambulating/patients/$patientId/PatientSectionLinks";
import { ImportantInfo } from "@/components/ImportantInfo/ImportantInfo";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import { humanFormat } from "@medoma/models/date-and-time";
import { isToday } from "date-fns";

const VisitDetails = () => {
  const { countryCode } = useUserProfile();
  const { routeId, visitId } = z
    .object({ routeId: z.string(), visitId: z.string() })
    .parse(useParams());
  const {
    data: visit,
    isPending,
    isError,
    error: getVisitError,
  } = useVisit(routeId, visitId);
  const { t } = useLingui();
  const { data: upcomingVisit } = useUpcomingVisit(routeId);
  const { data: ongoingVisit } = useOngoingVisit(routeId);

  const [isAddingNewActivityOccurrence, setIsAddingNewActivityOccurrence] =
    useState(false);

  type FormFields = {
    activityOccurrences: Record<IActivityOccurrence["id"], boolean>;
  };

  const { register, handleSubmit, watch, reset, setValue } =
    useForm<FormFields>();

  const latestVisitRef = useRef(visit);
  latestVisitRef.current = visit;

  useEffect(() => {
    const currentVisit = latestVisitRef.current;
    if (!currentVisit) {
      return;
    }
    reset({
      activityOccurrences: Object.fromEntries(
        currentVisit.occurrences.map((occurrence) => [
          occurrence.id,
          occurrence.status === activityOccurrenceStatusSchema.enum.finished,
        ]),
      ),
    });
  }, [reset, visit?.id, visit?.status]);

  const queryClient = useQueryClient();
  const {
    mutate: startTravellingToVisitMutation,
    isPending: isStartingTravellingToVisit,
  } = useMutation({
    mutationFn: ({ routeId, visitId }: { routeId: string; visitId: string }) =>
      startTravellingToVisit(routeId, visitId),
    onSuccess: () => {
      return queryClient.refetchQueries({ queryKey: routeKeys.all });
    },
    onError: (error) => {
      // UI out of sync. Reset cache and move on.
      if (
        isErrorWithKnownErrorCode(error) &&
        (error.data.code ===
          knownErrorCodeSchema.enum.AlreadyTravellingToVisit ||
          error.data.code === knownErrorCodeSchema.enum.AlreadyTravelledToVisit)
      ) {
        Sentry.captureException(
          "Failed to start travelling. Trip was already started. Refetching.",
        );
        return queryClient.refetchQueries({ queryKey: routeKeys.all });
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att påbörja resan till besöket.`} ${deducedError(error)}`,
        );
      }
    },
  });

  const { mutate: startVisitMutation, isPending: isStartingVisit } =
    useMutation({
      mutationFn: ({
        routeId,
        visitId,
      }: {
        routeId: string;
        visitId: string;
      }) => startVisit(routeId, visitId),
      onSuccess: () => {
        return queryClient.refetchQueries({ queryKey: routeKeys.all });
      },
      onError: (error) => {
        if (
          isErrorWithKnownErrorCode(error) &&
          (error.data.code === knownErrorCodeSchema.enum.OngoingVisit ||
            error.data.code === knownErrorCodeSchema.enum.FinishedVisit)
        ) {
          Sentry.captureException(
            "Failed to start visit. Visit was already started. Refetching.",
          );
          return queryClient.refetchQueries({ queryKey: routeKeys.all });
        } else {
          displayErrorMessageAlert(
            `${t`Det gick inte att påbörja besöket.`} ${deducedError(error)}`,
          );
        }
      },
    });

  const {
    mutate: finishCareEpisodeOccurrencesAndVisitMutation,
    isPending: isFinishingVisit,
  } = useMutation({
    mutationFn: async ({
      routeId,
      visitId,
      careEpisodeId,
      occurrenceIds,
    }: {
      routeId: string;
      visitId: string;
      careEpisodeId: string;
      occurrenceIds: string[];
    }) => {
      if (occurrenceIds.length > 0) {
        await finishCareEpisodeActivityOccurrences({
          careEpisodeId,
          occurrenceIds,
        });
      }
      await finishVisit(routeId, visitId);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: routeKeys.all });
    },
    onError: (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.FinishedVisit
      ) {
        Sentry.captureException(
          "Failed to finish visit. Visit was already finished. Refetching.",
        );

        return queryClient.refetchQueries({ queryKey: routeKeys.all });
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att avsluta besöket.`} ${deducedError(error)}`,
        );
      }
    },
  });

  const { mutate: cancelTravellingToVisitMutation } = useMutation({
    mutationFn: ({ routeId, visitId }: { routeId: string; visitId: string }) =>
      cancelTravellingToVisit(routeId, visitId),
    onSuccess: () => {
      return queryClient.refetchQueries({
        queryKey: visitKeys(routeId).detail(visitId),
      });
    },
    onError: (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.UnstartedTripToVisit
      ) {
        Sentry.captureException(
          "Failed to cancel travelling to visit. Visit was not being travelled to. Refetching.",
        );
        return queryClient.refetchQueries({ queryKey: routeKeys.all });
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att avbryta resan till besöket.`} ${deducedError(error)}`,
        );
      }
    },
  });

  const { mutate: cancelStartedVisitMutation } = useMutation({
    mutationFn: ({ routeId, visitId }: { routeId: string; visitId: string }) =>
      cancelStartedVisit(routeId, visitId),
    onSuccess: () => {
      return queryClient.refetchQueries({
        queryKey: visitKeys(routeId).detail(visitId),
      });
    },
    onError: (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        error.data.code === knownErrorCodeSchema.enum.UnstartedVisit
      ) {
        Sentry.captureException(
          "Failed to cancel started visit. Visit was not started. Refetching.",
        );
        return queryClient.refetchQueries({ queryKey: routeKeys.all });
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att avbryta besöket.`} ${deducedError(error)}`,
        );
      }
    },
  });

  const { mutate: revertFinishedVisitMutation } = useMutation({
    mutationFn: ({ routeId, visitId }: { routeId: string; visitId: string }) =>
      revertFinishedVisit(routeId, visitId),
    onSuccess: () => {
      return queryClient.invalidateQueries();
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ångra det slutförda besöket.`} ${deducedError(error)}`,
      );
    },
  });

  if (isPending) {
    return <Loading message={t`Laddar besöksdetaljer`} />;
  }

  if (isError) {
    Sentry.captureException(getVisitError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta besöksdetaljerna.`} ${deducedError(getVisitError)}`}
      />
    );
  }

  const submitActivityOccurrenceChecklistAndFinishVisit = handleSubmit(
    (data) => {
      const careEpisodeId = visit.patientId;
      const alreadyFinishedOccurrenceIds = new Set(
        visit.occurrences
          .filter(
            (occurrence) =>
              occurrence.status ===
              activityOccurrenceStatusSchema.enum.finished,
          )
          .map((occurrence) => occurrence.id),
      );

      const notCheckedOccurrenceIds = Object.entries(data.activityOccurrences)
        .filter(
          ([id, isChecked]) =>
            !isChecked && !alreadyFinishedOccurrenceIds.has(id),
        )
        .map(([id]) => id);

      const newlyCheckedOccurrenceIds = Object.entries(data.activityOccurrences)
        .filter(
          ([id, isChecked]) =>
            isChecked && !alreadyFinishedOccurrenceIds.has(id),
        )
        .map(([id]) => id);

      const allChecked = notCheckedOccurrenceIds.length === 0;

      if (!allChecked) {
        const notCheckedOccurrenceTitles = visit.occurrences
          .filter((occurrence) =>
            notCheckedOccurrenceIds.includes(occurrence.id),
          )
          .map(({ title }) => title)
          .join("\n- ");

        const TWO_LINE_BREAKS = "\n\n";
        const confirmText = t`Alla aktiviteter är inte utförda:${TWO_LINE_BREAKS}- ${notCheckedOccurrenceTitles}${TWO_LINE_BREAKS}Vill du ändå slutföra ditt besök?`;

        const confirmed = window.confirm(confirmText);

        if (confirmed) {
          finishCareEpisodeOccurrencesAndVisitMutation({
            routeId,
            visitId,
            careEpisodeId,
            occurrenceIds: newlyCheckedOccurrenceIds,
          });
        }

        // No confirmation given, do nothing
        return;
      }

      finishCareEpisodeOccurrencesAndVisitMutation({
        routeId,
        visitId,
        careEpisodeId,
        occurrenceIds: newlyCheckedOccurrenceIds,
      });
    },
  );

  const visitStartedTime = visit.startedAt
    ? humanFormat(visit.startedAt, { timeStyle: "short" })
    : null;
  const visitFinishedAt = visit.finishedAt
    ? humanFormat(visit.finishedAt, { timeStyle: "short" })
    : null;
  const visitTitle =
    visit.status === "ongoing" && visit.startedAt && visitStartedTime
      ? t`Besök pågår sedan ${visitStartedTime}`
      : visit.status === "finished" && visit.startedAt && visitStartedTime
        ? t`Besök startat ${visitStartedTime}`
        : t`Besök`;

  const finishedTitle =
    visit.finishedAt && visitFinishedAt ? t`Klar ${visitFinishedAt}` : t`Klar`;

  const firstOccurrence = visit.occurrences[0];
  const isVisitNotToday = firstOccurrence
    ? !isToday(firstOccurrence.start)
    : false;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const activityOccurrences = watch("activityOccurrences");
  const allActivitiesAreChecked = activityOccurrences
    ? Object.values(activityOccurrences).every(Boolean)
    : false;

  const selectAllActivities = () => {
    visit.occurrences.forEach((occurrence) => {
      setValue(`activityOccurrences.${occurrence.id}`, true);
    });
  };

  return (
    <div className={styles.visitDetailsWrapper}>
      {ongoingVisit && ongoingVisit.id !== visit.id ? (
        <div className={styles.statusBarWrapper}>
          <StatusBar>
            <Link to={`../${ongoingVisit.id}`} relative="path">{`${
              ongoingVisit.status === "travellingTo"
                ? t`Pågående resa till`
                : t`Pågående besök hos`
            } ${getPatientNameWithStatus(ongoingVisit.patient, "full")}`}</Link>
          </StatusBar>
        </div>
      ) : (
        <></>
      )}
      <article className={styles.visitDetails}>
        <header className={styles.visitDetailsHeader}>
          <div className={styles.nameAndPnr}>
            <Heading level="h2">
              {getPatientNameWithStatus(visit.patient, "full")}
            </Heading>
            {visit.patient.status ===
            patientStatusSchema.enum.deleted ? null : (
              <Text element="div" size="large">
                {formatPersonalIdentityNumber({
                  personalIdentityNumber: visit.patient.personalIdentityNumber,
                  personalIdentityNumberType:
                    visit.patient.personalIdentityNumberType,
                })}
              </Text>
            )}
          </div>
          {visit.patient.status === patientStatusSchema.enum.deleted ? null : (
            <CallPatientOrRelatives
              patient={{
                ...visit.patient,
                name: getPatientFullName(visit.patient.name),
                phoneNumber: visit.patient.phoneNumber
                  ? localizePhoneNumber({
                      countryCode,
                      phoneNumber: visit.patient.phoneNumber,
                    })
                  : undefined,
              }}
              relatives={visit.patient.relatives
                .filter(
                  ({ phoneNumber }) =>
                    phoneNumber !== null && phoneNumber !== undefined,
                )
                .map(({ name, relation, phoneNumber }) => ({
                  name: `${name} (${relation})`,
                  phoneNumber: localizePhoneNumber({
                    countryCode,
                    phoneNumber: phoneNumber!,
                  }),
                }))}
            />
          )}
        </header>
        {visit.patient.status !== patientStatusSchema.enum.deleted ? (
          <ImportantInfo patient={visit.patient} />
        ) : null}
        <Timeline>
          <TimelineItem
            title={visit.status === "travellingTo" ? t`Resa pågår` : t`Resa`}
            state={
              visit.status === "planned"
                ? "initial"
                : visit.status === "travellingTo"
                  ? "ongoing"
                  : "finished"
            }
            relatedAction={
              visit.status === "travellingTo"
                ? {
                    label: t`Avbryt`,
                    onClick: () => {
                      const confirmed = window.confirm(
                        t`Är du säker på att du vill avbryta resan till besöket?`,
                      );
                      if (confirmed) {
                        cancelTravellingToVisitMutation({
                          routeId: visit.routeId,
                          visitId: visit.id,
                        });
                      }
                    },
                  }
                : undefined
            }
          >
            <div className={styles.timelineItemContent}>
              {visit.status === "planned" ? (
                <>
                  {visit.patient.status !== patientStatusSchema.enum.deleted ? (
                    <p>{`${visit.patient.address.addressLine1}, ${visit.patient.address.city}`}</p>
                  ) : null}
                  {isVisitNotToday ? null : (
                    <FilledButton
                      onClick={() =>
                        startTravellingToVisitMutation({
                          routeId: visit.routeId,
                          visitId: visit.id,
                        })
                      }
                      disabled={isStartingTravellingToVisit}
                      width="fill-container"
                    >
                      <Trans>Starta resa</Trans>
                    </FilledButton>
                  )}
                </>
              ) : visit.status === "travellingTo" ? (
                <>
                  {visit.patient.status !== patientStatusSchema.enum.deleted ? (
                    <div className={styles.gpsNavigation}>
                      <PinIcon />
                      <Link
                        type="external"
                        // https://developers.google.com/maps/documentation/urls/get-started#directions-action
                        to={`https://www.google.com/maps/dir/?api=1&destination=${visit.patient.address.coordinates.latitude},${visit.patient.address.coordinates.longitude}&travelmode=driving`}
                      >
                        {`${visit.patient.address.addressLine1}, ${visit.patient.address.city}`}
                      </Link>
                    </div>
                  ) : null}
                  <OutlinedButton
                    onClick={() =>
                      startVisitMutation({
                        routeId: visit.routeId,
                        visitId: visit.id,
                      })
                    }
                    disabled={isStartingVisit}
                    width="fill-container"
                  >
                    <Trans>Påbörja besök</Trans>
                  </OutlinedButton>
                </>
              ) : (
                <></>
              )}
            </div>
          </TimelineItem>
          <TimelineItem
            title={visitTitle}
            state={
              visit.status === "planned" || visit.status === "travellingTo"
                ? "initial"
                : visit.status === "ongoing"
                  ? "ongoing"
                  : "finished"
            }
            relatedAction={
              visit.status === "ongoing"
                ? {
                    label: t`Avbryt`,
                    onClick: () => {
                      const confirmed = window.confirm(
                        t`Är du säker på att du vill avbryta besöket?`,
                      );
                      if (confirmed) {
                        cancelStartedVisitMutation({
                          routeId: visit.routeId,
                          visitId: visit.id,
                        });
                      }
                    },
                  }
                : undefined
            }
          >
            {visit.status === visitStatusSchema.enum.ongoing ? (
              <form
                className={styles.activityOccurrenceChecklist}
                onSubmit={submitActivityOccurrenceChecklistAndFinishVisit}
              >
                {visit.occurrences.map((occurrence) => (
                  <Checkbox
                    key={occurrence.id}
                    {...register(`activityOccurrences.${occurrence.id}`)}
                    label={{
                      text: occurrence.title,
                      component: (
                        <ActivityOccurrenceListItem occurrence={occurrence} />
                      ),
                    }}
                  />
                ))}
                <div className={styles.actionButtons}>
                  <PlainButton
                    onClick={() => setIsAddingNewActivityOccurrence(true)}
                  >
                    <PlusIcon />
                    <Trans>Lägg till</Trans>
                  </PlainButton>
                  <PlainButton onClick={selectAllActivities}>
                    <CheckmarkIcon />
                    <Trans>Markera alla aktiviteter som utförda</Trans>
                  </PlainButton>
                </div>
                {allActivitiesAreChecked ? (
                  <FilledButton
                    width="fill-container"
                    type="submit"
                    disabled={isFinishingVisit}
                  >
                    <Trans>Slutför besök</Trans>
                  </FilledButton>
                ) : (
                  <OutlinedButton
                    width="fill-container"
                    type="submit"
                    disabled={isFinishingVisit}
                  >
                    <Trans>Slutför besök</Trans>
                  </OutlinedButton>
                )}
              </form>
            ) : (
              <ul className={styles.activityOccurrenceList}>
                {visit.occurrences.map((occurrence) => (
                  <li key={`${occurrence.activityId}${occurrence.id}`}>
                    <ActivityOccurrenceListItem
                      occurrence={occurrence}
                      showStatus={
                        visit.status === visitStatusSchema.enum.finished
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </TimelineItem>
          <TimelineItem
            title={finishedTitle}
            state={
              visit.status === "planned" ||
              visit.status === "travellingTo" ||
              visit.status === "ongoing"
                ? "initial"
                : "finished"
            }
            relatedAction={
              visit.status === "finished"
                ? {
                    label: t`Ångra`,
                    onClick: () => {
                      const confirmed = window.confirm(
                        t`Är du säker på att du vill ångra det slutförda besöket?`,
                      );
                      if (confirmed) {
                        revertFinishedVisitMutation({
                          routeId: visit.routeId,
                          visitId: visit.id,
                        });
                      }
                    },
                  }
                : undefined
            }
          >
            {visit.status === "finished" ? (
              <span className={styles.upcomingVisitLinkWrapper}>
                {upcomingVisit ? (
                  <Link to={`../${upcomingVisit.id}`} relative="path">
                    <Trans>Gå till nästa besök</Trans>
                  </Link>
                ) : (
                  <Link to={`..`}>
                    <Trans>Gå tillbaka till listan</Trans>
                  </Link>
                )}
              </span>
            ) : (
              <></>
            )}
          </TimelineItem>
        </Timeline>
      </article>

      {visit.patient.status === patientStatusSchema.enum.prospect ? (
        <ProspectActions />
      ) : null}

      <PatientSectionLinks patientId={visit.patientId} />

      <DialogPanel
        isOpen={isAddingNewActivityOccurrence}
        onClose={() => setIsAddingNewActivityOccurrence(false)}
        title={t`Efterregistrera aktivitet`}
        position="bottom"
      >
        <PostRegisterActivity
          onSuccess={async () => {
            setIsAddingNewActivityOccurrence(false);
            return queryClient.invalidateQueries();
          }}
          patientId={visit.patient.id}
          routeId={visit.routeId}
          visitId={visitId}
        />
      </DialogPanel>
    </div>
  );
};

export default VisitDetails;
